import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@repo/ui/components/button';
import { FormError } from '@repo/ui/components/form-error';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/sheet';
import { Textarea } from '@repo/ui/components/textarea';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export interface WishlistItem {
  title: string;
  description?: string;
  link?: string;
}

const MAX_ITEMS = 3;

const wishlistItemSchema = z.object({
  title: z.string().min(1, 'O título é obrigatório'),
  description: z.string().optional(),
  link: z.string().url('URL inválida').optional().or(z.literal('')),
});

type WishlistItemFormValues = z.infer<typeof wishlistItemSchema>;

interface WishlistStepProps {
  title?: string;
  subtitle?: string;
  defaultValue?: WishlistItem[];
  isLoading?: boolean;
  continueLabel?: string;
  skipLabel?: string;
  loadingLabel?: string;
  onNext: (items: WishlistItem[]) => void;
  onBack: () => void;
}

export function WishlistStep({
  title = 'O que você gostaria de ganhar?',
  subtitle = `Adicione até ${MAX_ITEMS} itens à sua lista. Esta etapa é opcional.`,
  defaultValue = [],
  isLoading = false,
  continueLabel = 'Continuar',
  skipLabel = 'Pular',
  loadingLabel = 'Salvando...',
  onNext,
  onBack,
}: WishlistStepProps) {
  const [items, setItems] = useState<WishlistItem[]>(defaultValue);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WishlistItemFormValues>({
    resolver: zodResolver(wishlistItemSchema),
  });

  const addItem = (data: WishlistItemFormValues) => {
    const newItem: WishlistItem = {
      title: data.title,
      description: data.description || undefined,
      link: data.link || undefined,
    };
    setItems([...items, newItem]);
    reset();
    setIsModalOpen(false);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    onNext(items);
  };

  const handleSkip = () => {
    onNext([]);
  };

  const canAddMore = items.length < MAX_ITEMS;

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-muted-foreground text-sm">{subtitle}</p>
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex items-start justify-between rounded-lg border p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{item.title}</p>
                {item.description && (
                  <p className="text-muted-foreground truncate text-sm">
                    {item.description}
                  </p>
                )}
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary truncate text-sm hover:underline"
                  >
                    Ver link
                  </a>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isLoading}
                onClick={() => removeItem(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {canAddMore && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={isLoading}
          onClick={() => setIsModalOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar item ({items.length}/{MAX_ITEMS})
        </Button>
      )}

      <Sheet
        open={isModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            reset();
          }
          setIsModalOpen(open);
        }}
      >
        <SheetContent
          side="bottom"
          className="h-auto max-h-[90vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Adicionar item</SheetTitle>
            <SheetDescription>
              Adicione algo que você gostaria de ganhar.
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit(addItem)} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="item-title">O que você quer?</Label>
              <Input
                id="item-title"
                placeholder="Ex: Fone de ouvido bluetooth"
                disabled={isLoading}
                {...register('title')}
              />
              <FormError message={errors.title?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-description">Descrição (opcional)</Label>
              <Textarea
                id="item-description"
                placeholder="Detalhes como cor, tamanho, modelo..."
                rows={2}
                disabled={isLoading}
                {...register('description')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-link">Link (opcional)</Label>
              <Input
                id="item-link"
                type="url"
                placeholder="https://..."
                disabled={isLoading}
                {...register('link')}
              />
              <FormError message={errors.link?.message} />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isLoading}
                onClick={() => {
                  reset();
                  setIsModalOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" disabled={isLoading}>
                Adicionar
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
          disabled={isLoading}
        >
          Voltar
        </Button>
        {items.length > 0 ? (
          <Button
            type="button"
            className="flex-1"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? loadingLabel : continueLabel}
          </Button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={handleSkip}
            disabled={isLoading}
          >
            {isLoading ? loadingLabel : skipLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
